/**
 * Cliente HTTP da API.
 *
 * O token fica em memória e em sessionStorage — não em localStorage. Fechar a aba encerra a
 * sessão, o que é o comportamento esperado de um sistema interno usado em máquina compartilhada.
 */
import { loginComRetorno } from './retorno';

const TOKEN_KEY = 'es.workspace.token';
export const PERFIL_VISUALIZADO_KEY = 'es.workspace.perfil-visualizado';
const PERFIS_SIMULADOS = new Set(['RH', 'GESTOR', 'COLABORADOR']);

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Envia FormData sem serializar nem definir Content-Type. */
  formData?: FormData;
  /** Devolve o corpo bruto, para download de arquivo. */
  raw?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const perfilVisualizado = sessionStorage.getItem(PERFIL_VISUALIZADO_KEY);
  if (token && path !== '/me' && perfilVisualizado && PERFIS_SIMULADOS.has(perfilVisualizado)) {
    headers['X-View-As-Role'] = perfilVisualizado;
  }
  if (!options.formData && options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.formData
      ? { body: options.formData }
      : options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 401) {
    // Uma validação de sessão antiga pode terminar depois de um novo login. Nesse caso,
    // o 401 pertence ao token anterior e jamais deve apagar a sessão recém-criada.
    if (getToken() === token) {
      setToken(null);
      // Recarrega para o roteador devolver a tela de acesso.
      if (!location.pathname.startsWith('/entrar')) {
        location.assign(loginComRetorno(location.pathname + location.search));
      }
    }
    throw new ApiError('Sessão expirada. Entre novamente.', 401);
  }

  if (!response.ok) {
    let message = `Falha na requisição (${response.status}).`;
    let code: string | undefined;
    try {
      const body = (await response.json()) as { message?: string; code?: string };
      if (body.message) message = body.message;
      code = body.code;
    } catch {
      /* resposta sem JSON: mantém a mensagem padrão */
    }
    throw new ApiError(message, response.status, code);
  }

  if (options.raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', formData }),
  download: (path: string, body: unknown) =>
    request<Response>(path, { method: 'POST', body, raw: true }),
  /** GET que devolve um arquivo; salva no computador e devolve o nome usado. */
  baixar: async (path: string, nomePadrao: string): Promise<string> => {
    const resposta = await request<Response>(path, { raw: true });
    const disposition = resposta.headers.get('Content-Disposition') ?? '';
    const nome = /filename="([^"]+)"/.exec(disposition)?.[1] ?? nomePadrao;
    const url = URL.createObjectURL(await resposta.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
    URL.revokeObjectURL(url);
    return nome;
  },
};

// ---------------------------------------------------------------------------
// Tipos das respostas usadas pelas telas
// ---------------------------------------------------------------------------

export interface SessionUser {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
  employeeId: string | null;
  permissions: string[];
}

export interface Desk {
  id: string;
  code: string;
  floorNumber: number;
  floorLabel: string;
  officeSlug: string;
  officeName: string;
  zone: string | null;
  block: string | null;
  x: number;
  y: number;
  /** AREA_COMUM: copa. Tem QR e selo na planta, mas fica fora da escala e não registra presença. */
  kind: 'MAPEADA' | 'PARTICULAR' | 'AREA_COMUM';
  status: 'ATIVA' | 'INATIVA' | 'MANUTENCAO';
  bookable: boolean;
  needsReview: boolean;
  reviewReason: string | null;
}

export interface Office {
  slug: string;
  name: string;
  floors: Array<{ number: number; label: string }>;
}

export type DeskAvailability = Desk & {
  availability: 'LIVRE' | 'OCUPADA' | 'BLOQUEADA' | 'NAO_ESCALAVEL';
  occupiedBy: { employeeId: string; name: string; bookingId: string } | null;
};

export interface FloorCapacity {
  floorNumber: number;
  floorLabel: string;
  /** Só mesas comuns: a particular não entra na disponibilidade geral. */
  total: number;
  occupied: number;
  free: number;
  /** Ausentes em respostas antigas da API. */
  particularTotal?: number;
  particularOccupied?: number;
}

export interface ProximityCandidate {
  deskIds: string[];
  deskCodes: string[];
  contiguous: boolean;
  internalEdges: number;
  maxHopDistance: number;
  zones: string[];
  score: number;
}

export interface ProximityResult {
  candidates: ProximityCandidate[];
  degraded: boolean;
  degradedReason: string | null;
  availableCount: number;
}

export interface WeekCell {
  date: string;
  bookingId: string;
  deskCode: string;
  floorNumber: number;
  floorLabel: string;
  note: string | null;
}

export interface WeekRow {
  employeeId: string;
  employeeName: string;
  /** Gestor direto; ausente em respostas antigas da API. */
  managerId?: string | null;
  managerName: string | null;
  department: string | null;
  /** Colaborador da Convenia, colaborador novo ou visitante; ausente em respostas antigas. */
  kind?: PessoaKind;
  days: Record<string, WeekCell | null>;
}

export type PessoaKind = 'COLABORADOR' | 'COLABORADOR_NOVO' | 'VISITANTE';

/** Colaborador novo ou visitante cadastrado à parte no agendamento de mesa. */
export interface PessoaAvulsa {
  employeeId: string;
  employeeName: string;
  kind: Exclude<PessoaKind, 'COLABORADOR'>;
}

export interface WeekView {
  weekStart: string;
  weekEnd: string;
  days: string[];
  rows: WeekRow[];
  totalBookings: number;
}

/** Resposta de POST /reservations/:id/cancel. */
export interface CancelReservationResult {
  id: string;
  status: 'CANCELADA';
  /** Aviso ao colaborador. Falhar aqui não desfaz o cancelamento. */
  notice: { status: 'ENVIADO' | 'FALHOU' | 'SEM_EMAIL'; detail: string | null };
}

/** Líder que pode ser escolhido no filtro de equipe do painel. */
export interface TeamOption {
  employeeId: string;
  name: string;
  department: string | null;
  /** 0 = o próprio usuário (ou lista plana, para RH/administrador); 1 = coordenador… */
  depth: number;
  directReports: number;
  isSelf: boolean;
}

export interface TeamOptionsView {
  scope: 'TODOS' | 'EQUIPE' | 'PROPRIO';
  options: TeamOption[];
}

// ---------------------------------------------------------------------------
// Presença por QR
// ---------------------------------------------------------------------------

export type PresenceState =
  'CONFIRMADO' | 'PENDENTE' | 'SEM_CHECKIN' | 'FORA_DA_ESCALA' | 'NAO_ESCALADO';

export interface PresenceDesk {
  deskId: string;
  code: string;
  floorLabel: string;
  officeSlug: string;
  officeName: string;
}

export interface PresenceRow {
  employeeId: string;
  employeeName: string;
  department: string | null;
  managerName: string | null;
  isMe: boolean;
  state: PresenceState;
  checkin: {
    id: string;
    at: string;
    desk: PresenceDesk | null;
    source: 'QR' | 'IMPORTACAO';
  } | null;
  scheduled: PresenceDesk | null;
  divergent: boolean;
  attempt: { id: string; at: string; desk: PresenceDesk | null } | null;
}

/** Resposta de GET /presence?date=… */
export interface PresenceDay {
  date: string;
  today: string;
  scope: 'TODOS' | 'EQUIPE' | 'PROPRIO';
  me: PresenceRow | null;
  rows: PresenceRow[];
}

/** Resposta de GET /presence/qr/:token */
export interface QrDesk {
  desk: PresenceDesk;
  today: string;
  /** Nulo para conta sem colaborador: não confirma presença. */
  person: { employeeId: string; name: string; email: string } | null;
  state: PresenceState | null;
  confirmedAt: string | null;
  confirmedDesk: PresenceDesk | null;
  scheduled: PresenceDesk | null;
}

/** Resposta de GET /presence/reminders */
export interface CheckinReminderPreview {
  date: string;
  configured: boolean;
  scheduleEnabled: boolean;
  holiday: string | null;
  safetySink: { enabled: boolean; address: string | null };
  recipients: Array<{
    employeeId: string;
    name: string;
    email: string;
    desk: { code: string; floorLabel: string; officeName: string };
    alreadySent: boolean;
  }>;
  withoutEmail: Array<{ employeeId: string; name: string }>;
  sampleMessage: string | null;
}

/** Resposta de POST /presence/reminders/send */
export interface CheckinReminderResult {
  date: string;
  holiday: string | null;
  sent: number;
  failed: number;
  skipped: number;
  withoutEmail: number;
  failures: Array<{ name: string; reason: string }>;
}

/** Resposta de POST /presence/qr/:token/confirm */
export interface ConfirmPresence {
  outcome: 'CONFIRMADO' | 'JA_CONFIRMADO' | 'FORA_DA_ESCALA';
  at: string;
  desk: PresenceDesk;
  scheduled: PresenceDesk | null;
}

export interface MeetingRoomBooking {
  id: string;
  /** HH:MM, intervalo semiaberto [start, end). */
  start: string;
  end: string;
  title: string;
  createdBy: { name: string };
  /** Quem reservou, ou RH/administrador. */
  canCancel: boolean;
}

export interface MeetingRoom {
  id: string;
  name: string;
  officeSlug: string;
  officeName: string;
  floorNumber: number | null;
  /** Ex.: "11º andar · Torre A (Nove de Julho)". Nulo quando o prédio tem um andar só. */
  location: string | null;
  bookings: MeetingRoomBooking[];
}

/** Resposta de GET /meeting-rooms?date=… */
export interface MeetingRoomDay {
  date: string;
  window: { start: string; end: string; slotMinutes: number };
  rooms: MeetingRoom[];
}

export interface CheckinPreview {
  arquivo: { nome: string; hash: string; tamanhoBytes: number; aba: string };
  periodo: { inicio: string | null; fim: string | null };
  totais: {
    linhas: number;
    usuariosDistintos: number;
    checkinsValidos: number;
    diasPresenciaisDistintos: number;
    duplicados: number;
    naoIdentificados: number;
    rejeitados: number;
  };
  jaImportado: { hash: string; importadoEm: string; status: string } | null;
  identificacao: { porCpf: number; porAlias: number; porNome: number; naoIdentificado: number };
  temColunaCpf: boolean;
  tiposElegiveis: string[];
  porData: Array<{ data: string; validos: number }>;
  porTipoRecurso: Array<{ tipo: string; validos: number }>;
  recursosDesconhecidos: string[];
  pendencias: Array<{ usuario: string; ocorrencias: number; sugestoes: string[] }>;
  amostra: Array<{
    rowIndex: number;
    usuario: string;
    /** Sempre mascarado. O documento completo nunca chega ao navegador. */
    cpfMascarado: string | null;
    recurso: string;
    recursoTipo: string;
    data: string | null;
    employeeName: string | null;
    identificadoPor: 'CPF' | 'ALIAS' | 'NOME' | null;
    situacao: string;
    motivo: string | null;
  }>;
}

export interface SwilePreview {
  apuracao: {
    periodo: { inicio: string; fim: string };
    politicas: { multiplicadorVT: string; valorDiarioVR: string };
    origem: { importacoes: number; eventosValidos: number };
    totais: { colaboradores: number; dias: number; vt: string; vr: string };
    bloqueios: Array<{ codigo: string; mensagem: string; colaborador?: string }>;
    avisos: Array<{ codigo: string; mensagem: string; colaborador?: string }>;
    linhas: Array<{
      employeeId: string;
      nome: string;
      cpfMascarado: string;
      diasApurados: number;
      diasAjuste: number;
      diasTotal: number;
      valorDiarioVT: string;
      /** Ausentes em respostas antigas da API. */
      valorDiarioVR?: string;
      fonteValores?: 'CONVENIA' | 'PERFIL';
      vt: string;
      vr: string;
      presencaNaoPrevista: number;
      escaladoSemCheckin: number;
    }>;
  };
  validacao: {
    ok: boolean;
    rowCount: number;
    totalVT: string;
    totalVR: string;
    issues: Array<{ severity: string; code: string; message: string; location?: string }>;
  };
  arquivo: { nomeSugerido: string; versao: number };
  podeGerar: boolean;
}

export interface EmployeeListItem {
  id: string;
  canonicalName: string;
  corporateEmail: string | null;
  department: string | null;
  managerName: string | null;
  jobTitle: string | null;
  teamName: string | null;
  costCenterName: string | null;
  status: 'ATIVO' | 'INATIVO';
  cpfMask: string | null;
  conveniaEmployeeId: string | null;
  needsReview: boolean;
  reviewReason: string | null;
  hiredAt: string | null;
  leftAt: string | null;
  origin: string;
  hasLogin: boolean;
}

export interface EmployeeListView {
  total: number;
  ativos: number;
  inativos: number;
  revisao: number;
  items: EmployeeListItem[];
}

export interface HierarchyMember {
  id: string;
  canonicalName: string;
  jobTitle: string | null;
  corporateEmail: string | null;
  department: string | null;
  status: 'ATIVO' | 'INATIVO';
  hasLogin: boolean;
  /** Liderados diretos, cada um com os seus, até o fim da árvore. */
  reports: HierarchyMember[];
}

export interface HierarchyView {
  /** GESTOR: liderados. COLEGAS: quem não lidera vê, só para consulta, os colegas do mesmo gestor. */
  modo: 'GESTOR' | 'COLEGAS';
  me: Omit<HierarchyMember, 'reports'>;
  gestor: Omit<HierarchyMember, 'reports'> | null;
  team: HierarchyMember[];
  totalDiretos: number;
  totalNaHierarquia: number;
}

export interface ConveniaEmployeeSnapshot {
  local: {
    id: string;
    canonicalName: string;
    corporateEmail: string | null;
    department: string | null;
    managerName: string | null;
    jobTitle: string | null;
    teamName: string | null;
    costCenterName: string | null;
    status: 'ATIVO' | 'INATIVO';
    cpfMask: string | null;
    conveniaEmployeeId: string;
    hiredAt: string | null;
    leftAt: string | null;
    lastImportAt: string | null;
    hasLogin: boolean;
  };
  queries: { detail: string; benefits: string };
  detail: {
    conveniaId: string;
    name: string | null;
    lastName: string | null;
    fullName: string | null;
    email: string | null;
    corporateEmail: string | null;
    department: string | null;
    supervisor: string | null;
    costCenter: string | null;
    team: string | null;
    jobTitle: string | null;
    hiringDate: string | null;
    registration: string | null;
    dismissalDate: string | null;
    hasCpfInConvenia: boolean;
    cpfMask: string | null;
    cpfSource: 'DETALHE_CONVENIA' | 'SINCRONIZACAO_CONVENIA' | 'AUSENTE';
  };
  benefits: Array<{
    id: string | null;
    benefitId: string | null;
    name: string | null;
    type: string | null;
    operator: string | null;
    employeeValue: string | null;
    companyValue: string | null;
    paymentMethod: string | null;
    mappedKind: 'VT' | 'VR' | 'MULTIBENEFICIOS' | null;
  }>;
  dependents: Array<{
    id: string | null;
    name: string | null;
    relation: string | null;
    benefits: Array<{ name: string | null; type: string | null }>;
  }>;
}

export interface UserItem {
  id: string;
  email: string;
  displayName: string;
  provider: 'LOCAL' | 'ENTRA_ID';
  isActive: boolean;
  employeeId: string | null;
  employeeName: string | null;
  roles: string[];
  lastLoginAt: string | null;
  createdAt: string;
}

/** Resultado de POST /users/provision-accounts. */
export interface ProvisioningSummary {
  colaboradoresAnalisados: number;
  contasCriadas: number;
  contasVinculadas: number;
  contasAtivadas: number;
  contasDesativadas: number;
  emailsAtualizados: number;
  pendencias: Array<{
    employeeId: string;
    nome: string;
    motivo: 'SEM_EMAIL_CORPORATIVO' | 'EMAIL_EM_OUTRA_CONTA';
  }>;
}

// ---------------------------------------------------------------------------
// Envio da escala por e-mail
// ---------------------------------------------------------------------------

export type ScheduleEmailMode =
  'ALL_WITH_BOOKINGS' | 'SELECTED_EMPLOYEES' | 'MANAGERS' | 'SIMULATION_TO_EMAIL';

export interface ScheduleEmailRequest {
  weekStart: string;
  mode: ScheduleEmailMode;
  employeeIds?: string[];
  /**
   * @deprecated Use `simulationEmails` (array). Mantido para retrocompatibilidade.
   * Obrigatório quando mode = SIMULATION_TO_EMAIL e `simulationEmails` não for enviado.
   */
  simulationEmail?: string;
  /** Lista de endereços que recebem o envio direcionado. Obrigatório quando mode = SIMULATION_TO_EMAIL. */
  simulationEmails?: string[];
  resend?: boolean;
  resendReason?: string;
}

export interface ScheduleEmailRecipient {
  employeeId: string | null;
  name: string;
  email: string;
  kind: 'COLABORADOR' | 'GESTOR' | 'SIMULACAO';
  days: number;
  alreadySent: boolean;
}

export interface ScheduleEmailMissing {
  employeeId: string;
  name: string;
  reason: string;
}

export interface ScheduleEmailPreview {
  week: { start: string; end: string; label: string };
  mode: ScheduleEmailMode;
  subject: string;
  recipients: ScheduleEmailRecipient[];
  withoutEmail: ScheduleEmailMissing[];
  totals: { recipients: number; alreadySent: number; withoutEmail: number; scheduledDays: number };
  sampleHtml: string;
  sampleFor: string | null;
  safetySink: { enabled: boolean; address: string | null };
  provider: string;
  canSend: boolean;
  blockReason: string | null;
}

export interface ScheduleEmailResult {
  sent: number;
  failed: number;
  skipped: number;
  deliveries: Array<{
    name: string;
    email: string;
    status: 'ENVIADO' | 'FALHOU' | 'IGNORADO';
    detail: string | null;
  }>;
}

export interface ConveniaSyncReport {
  totalConsultado: number;
  colaboradoresCriados: number;
  colaboradoresAtualizados: number;
  colaboradoresInativados: number;
  departamentosCriados: number;
  departamentosAtualizados: number;
  centrosCustoAtualizados: number;
  timesAtualizados: number;
  gestoresVinculados: number;
  gestoresNaoEncontrados: number;
  colaboradoresSemCpf: number;
  colaboradoresCpfInvalido: number;
  beneficiosEmpresaAtualizados: number;
  beneficiosColaboradorAtualizados: number;
  falhasApi: number;
  tempoTotalMs: number;
  falhas: Array<{ etapa: string; status?: number; motivo: string }>;
  /** Ausente em execuções anteriores à criação automática de contas. */
  contas?: {
    criadas: number;
    vinculadas: number;
    liberadas: number;
    desativadas: number;
    emailsAtualizados: number;
    pendencias: number;
  };
}

export interface ConveniaSyncStatus {
  configured: boolean;
  enabled: boolean;
  running: boolean;
  lastRun: {
    id: string;
    status: string;
    trigger: string;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    report: ConveniaSyncReport;
    errorMessage: string | null;
  } | null;
}
